import { bindSwiperAutoplayToViewport } from "../utils/bind-swiper-autoplay-to-viewport.js";

const MOBILE_MAX_PX = 767;
const MOBILE_MQ = `(max-width: ${MOBILE_MAX_PX}px)`;
const NORMAL_SPEED = 4000;
const CLONE_ATTR = "data-logo-marquee-clone";
/** Lower = slower while hovered (Web Animations playbackRate; avoids CSS duration restart). */
const MARQUEE_HOVER_PLAYBACK_RATE = 0.45;

export function logoSlider() {
  const media = window.matchMedia(MOBILE_MQ);
  /** @type {WeakMap<Element, { releaseViewport: () => void }>} */
  const viewportCleanupByWrapper = new WeakMap();
  /** @type {WeakMap<Element, AbortController>} */
  const marqueeHoverAbortByWrapper = new WeakMap();
  /** @type {WeakMap<Element, ResizeObserver>} */
  const marqueeResizeObserverByInner = new WeakMap();

  /**
   * @param {Element} wrapper
   */
  function unbindMarqueeHover(wrapper) {
    const ac = marqueeHoverAbortByWrapper.get(wrapper);
    if (ac) {
      ac.abort();
      marqueeHoverAbortByWrapper.delete(wrapper);
    }
  }

  /**
   * Slow marquee on hover without restarting the CSS animation (playbackRate).
   * @param {Element} wrapper
   * @param {Element} inner
   */
  function bindMarqueeHoverSlow(wrapper, inner) {
    unbindMarqueeHover(wrapper);

    if (typeof inner.getAnimations !== "function") {
      return;
    }

    const ac = new AbortController();
    marqueeHoverAbortByWrapper.set(wrapper, ac);

    const setMarqueePlaybackRate = (rate) => {
      inner.getAnimations().forEach((anim) => {
        if (anim.animationName === "logo-slider-marquee") {
          anim.playbackRate = rate;
        }
      });
    };

    wrapper.addEventListener(
      "mouseenter",
      () => {
        requestAnimationFrame(() =>
          setMarqueePlaybackRate(MARQUEE_HOVER_PLAYBACK_RATE),
        );
      },
      { signal: ac.signal },
    );

    wrapper.addEventListener(
      "mouseleave",
      () => {
        requestAnimationFrame(() => setMarqueePlaybackRate(1));
      },
      { signal: ac.signal },
    );
  }

  /**
   * @param {Element} inner
   */
  function disconnectMarqueeResize(inner) {
    const ro = marqueeResizeObserverByInner.get(inner);
    if (ro) {
      ro.disconnect();
      marqueeResizeObserverByInner.delete(inner);
    }
  }

  /**
   * With flex `gap`, total track width is not 2×half — there is an extra gap between
   * the last original slide and first clone, so -50% jumps at loop. Measure one loop in px.
   * @param {Element} inner
   */
  function updateMarqueeLoopDistance(inner) {
    const firstOriginal = inner.querySelector(
      `.swiper-slide:not([${CLONE_ATTR}])`,
    );
    const firstClone = inner.querySelector(`[${CLONE_ATTR}]`);
    if (!firstOriginal || !firstClone) {
      return;
    }

    const loopPx =
      firstClone.getBoundingClientRect().left -
      firstOriginal.getBoundingClientRect().left;

    if (loopPx > 0) {
      inner.style.setProperty(
        "--logo-marquee-distance",
        `${Math.round(loopPx * 1000) / 1000}px`,
      );
    }
  }

  /**
   * @param {Element} inner
   */
  function bindMarqueeLayout(inner) {
    disconnectMarqueeResize(inner);
    // Ensure layout after DOM changes before measuring
    inner.getBoundingClientRect();
    updateMarqueeLoopDistance(inner);

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const ro = new ResizeObserver(() => {
      updateMarqueeLoopDistance(inner);
    });
    ro.observe(inner);
    marqueeResizeObserverByInner.set(inner, ro);
  }

  /**
   * @param {Element} inner
   */
  function teardownMarquee(inner) {
    disconnectMarqueeResize(inner);
    inner.style.removeProperty("--logo-marquee-distance");
    inner.querySelectorAll(`[${CLONE_ATTR}]`).forEach((node) => node.remove());
    delete inner.dataset.marqueeDuplicated;
  }

  /**
   * @param {Element} wrapper
   * @param {Element} inner
   */
  function setupMarquee(wrapper, inner) {
    if (inner.dataset.marqueeDuplicated) {
      wrapper.classList.add("logo-slider__wrapper--marquee");
      inner.getBoundingClientRect();
      updateMarqueeLoopDistance(inner);
      return;
    }

    const originals = inner.querySelectorAll(
      `.swiper-slide:not([${CLONE_ATTR}])`,
    );
    originals.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute(CLONE_ATTR, "");
      inner.appendChild(clone);
    });

    inner.dataset.marqueeDuplicated = "true";
    wrapper.classList.add("logo-slider__wrapper--marquee");
    inner.getBoundingClientRect();
    updateMarqueeLoopDistance(inner);
  }

  /**
   * @param {Element} wrapper
   */
  function destroySwiper(wrapper) {
    const entry = viewportCleanupByWrapper.get(wrapper);
    if (entry?.releaseViewport) {
      entry.releaseViewport();
    }
    viewportCleanupByWrapper.delete(wrapper);

    const swiper = wrapper.swiper;
    if (swiper) {
      swiper.destroy(true, true);
    }
  }

  /**
   * @param {Element} wrapper
   */
  function initSwiper(wrapper) {
    if (wrapper.swiper || typeof Swiper === "undefined") {
      return;
    }

    const swiper = new Swiper(wrapper, {
      loop: true,
      speed: NORMAL_SPEED,
      spaceBetween: 16,
      preloadImages: false,
      slidesPerView: "auto",
      centeredSlides: true,
      allowTouchMove: false,   // Disable mouse/touch dragging
      simulateTouch: false,
      autoplay: {
        delay: 1,
        pauseOnMouseEnter: false,
      },
      breakpoints: {
        1024: {
          spaceBetween: 20,
        },
        1400: {
          spaceBetween: 23,
        },
      },
    });

    const releaseViewport = bindSwiperAutoplayToViewport(swiper);
    viewportCleanupByWrapper.set(wrapper, { releaseViewport });
  }

  function applyMode() {
    const isMobile = media.matches;

    document.querySelectorAll(".logo-swiper").forEach((wrapper) => {
      const inner = wrapper.querySelector(".logo-slider__inner");
      if (!inner) {
        return;
      }

      unbindMarqueeHover(wrapper);
      wrapper.classList.remove("logo-slider__wrapper--marquee");

      if (isMobile) {
        teardownMarquee(inner);
        destroySwiper(wrapper);
        initSwiper(wrapper);
      } else {
        destroySwiper(wrapper);
        teardownMarquee(inner);
        setupMarquee(wrapper, inner);
        requestAnimationFrame(() => {
          bindMarqueeLayout(inner);
          bindMarqueeHoverSlow(wrapper, inner);
        });
      }
    });
  }

  applyMode();

  if (typeof media.addEventListener === "function") {
    media.addEventListener("change", applyMode);
  } else {
    media.addListener(applyMode);
  }
}
